import type { RoleCode } from "./auth";

/**
 * TLC library (0057): the broadcast graphics TLC plays out — OGraf templates,
 * their Studio sources and thumbnails, the API lookups, the template contract
 * and the TLC app installer itself — kept in the private bucket instead of a
 * shared Google Drive folder. Venue TLCs sign in with their own account, list
 * the library and download what changed; a SUPER_ADMIN publishes.
 *
 * A file is addressed by its LIBRARY PATH, the same relative path it has in a
 * TLC Templates folder (`SEAG/ATH/ATH-Results/ath-results-1a2b3c.ograf.zip`,
 * `_app/latest.json`, `_lookups/lookups.json`): TLC mirrors the library into a
 * local folder with exactly that layout. Publishing a path again keeps the
 * previous versions as history (`TLC_LIBRARY_VERSIONS_KEPT`).
 */

/** Who may list and download the library: every role a TLC operator signs in with. */
export const TLC_LIBRARY_READ_ROLES = ["SUPER_ADMIN", "ADMIN", "MEDAL_DESK", "OPERATION", "ORGANIZATION"] as const satisfies readonly RoleCode[];
/** Who may publish or remove files — app releases and templates alike (user, 2026-10-25). */
export const TLC_LIBRARY_PUBLISH_ROLES = ["SUPER_ADMIN"] as const satisfies readonly RoleCode[];

/** `tlc_library_files.kind`, decided by the path (`tlcLibraryKindOf`), never by the client. */
export const TLC_LIBRARY_FILE_KINDS = ["TEMPLATE", "SOURCE", "THUMBNAIL", "DATA", "APP"] as const;
export type TlcLibraryFileKind = (typeof TLC_LIBRARY_FILE_KINDS)[number];

/** What each kind is stored and served as (`ck_tlc_library_files_content_type`). */
export const TLC_LIBRARY_CONTENT_TYPES = {
	TEMPLATE: "application/zip",
	SOURCE: "application/json",
	THUMBNAIL: "image/png",
	DATA: "application/json",
	APP: "application/vnd.microsoft.portable-executable",
} as const satisfies Record<TlcLibraryFileKind, string>;
export const TLC_LIBRARY_STORED_TYPES = ["application/zip", "application/json", "image/png", "application/vnd.microsoft.portable-executable"] as const;

const MiB = 1024 * 1024;
export const TLC_LIBRARY_MAX_BYTES = {
	TEMPLATE: 100 * MiB,
	SOURCE: 100 * MiB,
	THUMBNAIL: 10 * MiB,
	DATA: 10 * MiB,
	APP: 512 * MiB,
} as const satisfies Record<TlcLibraryFileKind, number>;
/** The largest file of any kind (`ck_tlc_library_files_size`). */
export const TLC_LIBRARY_FILE_MAX_BYTES = 512 * MiB;

/**
 * Uploads go up in parts of this size (the last one smaller). Two limits on
 * cdn.liyeon.dev decide it: Cloudflare refuses request bodies over 100 MB,
 * and every request is cut at 60 s (measured 2026-11-03: a part still
 * sending at 60.8 s fails "499 Client Closed Request"). A 64 MB part needed
 * ~1.1 MB/s sustained and the TLC installer failed on slower links; 16 MB
 * needs ~280 KB/s. The bucket's floor is 5 MiB (every part but the last).
 */
export const TLC_LIBRARY_PART_BYTES = 16 * MiB;

/** Versions of one path kept (the current one included); older ones are deleted on publish. */
export const TLC_LIBRARY_VERSIONS_KEPT = 3;

export const TLC_LIBRARY_PATH_MAX = 300;
export const TLC_LIBRARY_PATH_DEPTH_MAX = 6;

/**
 * One path segment: starts with a letter, digit or `_` (`_app`, `_lookups`),
 * then letters, digits, spaces and `._()&+,'-` — template folders are named
 * like "ATH-Athlete Up (Jumps and Throws)". No `..`, no `~Retired`, no
 * backslash, no trailing space or dot (Windows would drop it).
 */
const SEGMENT = /^[A-Za-z0-9_][A-Za-z0-9 ._()&+,'-]*$/;

/** Is this a library path the server accepts? */
export function isTlcLibraryPath(path: string): boolean {
	if (path.length === 0 || path.length > TLC_LIBRARY_PATH_MAX) return false;
	const parts = path.split("/");
	if (parts.length > TLC_LIBRARY_PATH_DEPTH_MAX) return false;
	return parts.every((s) => SEGMENT.test(s) && !s.endsWith(" ") && !s.endsWith(".") && !s.includes(".."));
}

/** What a path holds, from its name; null when the library does not take that file. */
export function tlcLibraryKindOf(path: string): TlcLibraryFileKind | null {
	const p = path.toLowerCase();
	if (p.startsWith("_app/") && p.endsWith(".exe") && !p.slice(5).includes("/")) return "APP";
	if (p.endsWith(".ograf.zip")) return "TEMPLATE";
	if (p.endsWith(".ogs")) return "SOURCE";
	if (p.endsWith(".png")) return "THUMBNAIL";
	if (p.endsWith(".json")) return "DATA";
	return null;
}

export const TLC_SHA256 = /^[0-9a-f]{64}$/;

export interface TlcLibraryFile {
	id: string;
	path: string;
	kind: TlcLibraryFileKind;
	content_type: string;
	size_bytes: number;
	/** Hex SHA-256 of the bytes, checked by the server on publish: what TLC compares to decide whether to download. */
	sha256: string;
	published_at: string;
	published_by: { id: string; name: string } | null;
}

export interface TlcLibraryUploadPart {
	part_number: number;
	/** Presigned PUT for exactly `size_bytes` bytes of the file, from `(part_number - 1) * part_size`. */
	url: string;
	size_bytes: number;
}

/** `POST /tlc/library/uploads`: the path already holds these bytes — nothing to upload. */
export interface TlcLibraryUnchanged {
	unchanged: true;
	file: TlcLibraryFile;
}

/** `POST /tlc/library/uploads`: PUT each part, then complete with the token. */
export interface TlcLibraryUploadStart {
	unchanged: false;
	file_id: string;
	part_size: number;
	parts: TlcLibraryUploadPart[];
	upload_token: string;
	expires_at: string;
}

export interface TlcLibraryFileUrl {
	url: string;
	expires_at: string;
}

/** `GET /tlc/library/files`: the current file of every path, in path order. */
export interface TlcLibraryPage {
	items: TlcLibraryFile[];
	/** Send back as `cursor` for the next page; null on the last one. */
	next_cursor: string | null;
}
